import { useTranslation } from "@i18n";
import React, { forwardRef, useEffect, useRef, useState, MutableRefObject, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { convertDate } from "@Services/luxonHelper";
import SlickGrid1, {
  type GridClickEvent,
  type GridRowBase,
  type QueryShape,
  type SlickGrid1Ref,
  type ToolbarItem,
  type ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import { MdEdit } from "react-icons/md";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "@Services/constants";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import _ from "lodash";
import ChecklistProperties from "./ChecklistProperties.jsx";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent.js";
import { manageChecklist } from "@Features/reviews/helpers";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import ToolTipComponent from "@CubeComponents/html/tooltip/index.js";
import { recordStatus } from "helpers/constants";
import IconComponent from "@CubeComponents/icons";
import { getChecklistDetailUrl } from "@Services/utils/helpers.js";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { getBackendUrl } from "@Utils";
import { GridPageLayout } from "@CubeComponents/html/GridPageLayout";
import type { RootState } from "@Reducers/index";
import type { ManageChecklistRequest } from "@ServerTypes/reviews/reviews.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { TranslatedString } from "@Helpers/types/i18n";

/** One answer choice of a checklist — `manageChecklistRequest.choices`, an open `{ value, ... }` record. */
type ChecklistChoice = NonNullable<ManageChecklistRequest["choices"]>[number];
/** One section of a checklist template — `checklistTemplateSectionSchema` in reviews.schema. */
type ChecklistSection = NonNullable<ManageChecklistRequest["template"]>[number];

/**
 * One checklist-template row of this grid. `getChecklists` returns its rows in a `result` typed
 * `z.unknown()` on the server (`reviewStatusResponse`), so there is no server row type; the fields
 * named here are the ones this grid, its properties panel and the checklist editor read.
 */
type ChecklistRow = GridRowBase & {
  _id?: string;
  title?: string;
  description?: string;
  createdBy?: { userId?: ObjectIdString; firstName?: string; lastName?: string };
  createdDate?: number;
  name?: string;
  isInUse?: boolean;
  template?: ChecklistSection[];
  choices?: ChecklistChoice[];
  allowMultiple?: boolean;
};

/**
 * Column shape of this grid. Declared locally rather than via `typedColumns<ChecklistRow>` because
 * wrapping the array in `typedColumns()` would add a call that is not in the emitted code today; the
 * array is bridged to the grid's branded column type at the prop instead.
 */
interface ChecklistGridColumn {
  id: string;
  name?: string;
  field: string;
  filterType?: "Text" | "Date";
  formatter?: (row: number, cell: number, value: unknown, columnDef: unknown, dataContext: ChecklistRow) => unknown;
  _valueGetter?: (row: ChecklistRow) => unknown;
}

/**
 * Payload the create / edit / delete modal is opened with: `{}` for "create", the single selected
 * row for "edit", and every selected row for "delete".
 */
type ChecklistModalDetails = ChecklistRow | ChecklistRow[];

type ChecklistModalState = {
  show: boolean;
  type: string | null;
  details: ChecklistModalDetails;
};

type CheckListDataProps = {
  setIsRowSelected?: (value: boolean) => void;
  showOnlyGrid?: boolean;
  /** Extra grid query, in the same envelope the `query` prop takes. No current caller passes it. */
  customQuery?: QueryShape;
  selectedProjectIds?: Array<string | number>;
};

const CheckListData = forwardRef<SlickGrid1Ref<ChecklistRow>, CheckListDataProps>(
  ({ setIsRowSelected, showOnlyGrid = false }, ref) => {
    const { t } = useTranslation();
    const localRef = useRef<SlickGrid1Ref<ChecklistRow> | null>(null);
    const dataGrid: MutableRefObject<SlickGrid1Ref<ChecklistRow> | null> =
      (ref as MutableRefObject<SlickGrid1Ref<ChecklistRow> | null>) || localRef;
    const [hasSelection, setHasSelection] = useState(false);
    const [checklistsQuery, setChecklistsQuery] = useState<QueryShape | null>(null);
    const [isLoadingChecklist, setIsLoadingChecklist] = useState(false);
    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

    const [modalState, setModalState] = useState<ChecklistModalState>({
      show: false,
      type: null,
      details: {},
    });
    const [createTitle, setCreateTitle] = useState("");
    const [createDescription, setCreateDescription] = useState("");
    const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [recordProperties, setRecordProperties] = useState<{ show: boolean; details: ChecklistRow }>({
      show: false,
      details: {},
    });

    const [selectedItems, setSelectedItems] = useState<ChecklistRow[]>([]);

    const { portalSettingInfo } = useSelector((state: RootState) => state);

    const handlePropertiesOpen = () => {
      const selected = dataGrid.current?.dataView?.getAllSelectedItems?.() ?? [];
      setSelectedItems(selected);
      setRecordProperties((prev) => ({
        show: !prev.show,
        // `length === 1` guarantees slot 0 exists.
        details: selected.length === 1 ? selected[0]! : {},
      }));
    };

    useEffect(() => {
      const initialQuery = buildBaseQuery();
      setChecklistsQuery(initialQuery);
    }, []);

    const buildBaseQuery = () => {
      const params: Record<string, unknown> = {};
      return { params };
    };

    const openModal = (type: string, details: ChecklistModalDetails = {}) => {
      setModalState({ show: true, type, details });
      if (type === "create") {
        setCreateTitle("");
        setCreateDescription("");
        setIsSubmittingCreate(false);
      } else if (type === "edit") {
        // The "edit" branch is only ever reached with the single selected row.
        setEditTitle((details as ChecklistRow).title || "");
        setEditDescription((details as ChecklistRow).description || "");
        setIsSubmittingEdit(false);
      }
    };

    const closeModal = () => setModalState({ show: false, type: null, details: {} });

    const handleEditOpen = () => {
      const selected = dataGrid.current!.dataView.getAllSelectedItems();
      // `length === 1` guarantees slot 0 exists.
      if (selected.length === 1) openModal("edit", selected[0]!);
    };

    const handleDeleteClick = () => {
      const selected = dataGrid.current!.dataView.getAllSelectedItems();
      if (selected.length >= 1) openModal("delete", selected);
    };

    const performDeleteOperation = async () => {
      setIsLoadingChecklist(true);
      // The "delete" branch is only ever reached with the array of selected rows, each of which came
      // back from Mongo with an `_id`.
      const selectedIds = (modalState.details as ChecklistRow[]).map((item) => item._id! as ObjectIdString);
      const data = {
        selectedIds,
        recordData: recordStatus.DELETED,
      };
      const response = await manageChecklist(data);
      dataGrid.current!.refresh!();
      if (response) {
        dispatch(
          showToastNotification({
            content: t("The selected checklist(s) have been deleted successfully."),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
      setIsLoadingChecklist(false);
      closeModal();
    };

    const handleSubmit = async (e?: FormEvent) => {
      e?.preventDefault();

      if (modalState.type === "edit") {
        if (!editTitle.trim()) return;
        const selectedId = (modalState.details as ChecklistRow)?._id;
        setIsSubmittingEdit(true);
        try {
          const data = {
            // Grid-row id; the manageChecklist route schema validates it server-side.
            _id: selectedId as ObjectIdString,
            title: editTitle.trim(),
            description: editDescription.trim(),
          };
          const response = await manageChecklist(data);
          dataGrid.current?.refresh?.();

          if (response.data?.status) {
            dispatch(
              showToastNotification({
                content: t("The changes have been made successfully!"),
                type: TOAST_TYPE_SUCCESS,
              }),
            );
          }
        } finally {
          setIsSubmittingEdit(false);
        }
      } else {
        if (!createTitle.trim()) return;
        setIsSubmittingCreate(true);
        try {
          const data = {
            title: createTitle.trim(),
            description: createDescription.trim(),
            allowMultiple: false,
            choices: [{ value: "Yes" }, { value: "No" }, { value: "N/A" }],
          };
          const response = await manageChecklist(data);
          dataGrid.current?.refresh?.();

          if (response.data?.status) {
            dispatch(
              showToastNotification({
                content: t("The checklist has been created successfully!"),
                type: TOAST_TYPE_SUCCESS,
              }),
            );
          }
        } finally {
          setIsSubmittingCreate(false);
        }
      }
      closeModal();
    };

    const createBtnTemplate = () => (
      <ButtonComponent
        startIcon={<AddRoundedIcon />}
        onClick={() => openModal("create")}
        className="px-4 py-1.5 rounded-md bg-gray-150 text-gray-800 hover:bg-gray-200 text-sm font-medium ms-2"
      >
        {t("Create")}
      </ButtonComponent>
    );

    const editBtnTemplate = () => (
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleEditOpen}>
        <div className="flex items-center gap-1">
          <MdEdit size={16} />
          <span>{t("Edit")}</span>
        </div>
      </ButtonComponent>
    );

    const deleteBtnTemplate = () => (
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleDeleteClick}>
        <div className="flex items-center gap-1">
          <DeleteIcon size={16} className={""} />
          <span>{t("Delete")}</span>
        </div>
      </ButtonComponent>
    );

    const toolbarForChecklists = [
      {
        id: "create_checklist",
        template: createBtnTemplate,
        disabled: !portalSettingInfo?.reviewFlowsChecklist?.addReviewFlowsChecklist?.enable,
      },
      {
        id: "edit_checklist",
        template: editBtnTemplate,
        disabled: !portalSettingInfo?.reviewFlowsChecklist?.editReviewFlowsChecklist?.enable || !hasSelection,
      },
      {
        id: "delete_checklist",
        template: deleteBtnTemplate,
        disabled: !portalSettingInfo?.reviewFlowsChecklist?.deleteReviewFlowsChecklist?.enable || !hasSelection,
      },
    ];

    const creatorTemplate = (args: ChecklistRow) => {
      const container = document.createElement("div");
      renderUserTag(container, { id: args.createdBy?.userId });
      return container;
    };

    const createdDateTemplate = (args: ChecklistRow) => {
      if (!args.createdDate) return "-";
      const dateFormat = convertDate(Number(args.createdDate), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <ToolTipComponent position="bottom" content={dateFormat}>
          <span>{convertDate(Number(args.createdDate), "dateForText")}</span>
        </ToolTipComponent>,
      );
      return container;
    };

    const [checklistGridColumns] = useState<ChecklistGridColumn[]>([
      { id: "title", name: t("Title"), field: "title", filterType: "Text" },
      { id: "description", name: t("Description"), field: "description", filterType: "Text" },
      {
        id: "createdBy",
        name: t("Created By"),
        field: "createdBy",
        filterType: "Text",
        formatter: (data: number, params: number, a: unknown, b: unknown, value: ChecklistRow) =>
          creatorTemplate(value),
        // KNOWN BUG, preserved verbatim: `createdBy` is dereferenced without a guard, so a row stored
        // without it (the field is not modelled by any server schema) makes sorting/filtering/export on
        // this column throw. The fix would be `params.createdBy?.firstName`, which changes emitted code.
        _valueGetter: (params: ChecklistRow) => `${params.createdBy!.firstName} ${params.createdBy!.lastName}`,
      },
      {
        id: "createdDate",
        name: t("Created On"),
        field: "createdDate",
        filterType: "Date",
        formatter: (data: number, params: number, a: unknown, b: unknown, value: ChecklistRow) =>
          createdDateTemplate(value),
        _valueGetter: (params: ChecklistRow) => convertDate(Number(params.createdDate), "dateForTooltip"),
      },
    ]);

    const modalHeader = () => {
      const titles: Record<string, TranslatedString> = {
        create: t("Create Checklist"),
        edit: t("Edit Checklist"),
        delete: t("Delete Checklist(s)"),
      };
      return (
        <div className="text-2xl font-semibold leading-[120%] text-[var(--Text-text-primary,#192048)]">
          {/* `modalHeader` is only rendered while a modal is open, so `type` is set. */}
          {titles[modalState.type!]}
        </div>
      );
    };

    const modalContent = () => {
      switch (modalState.type) {
        case "create":
        case "edit": {
          const isEdit = modalState.type === "edit";
          const titleValue = isEdit ? editTitle : createTitle;
          const descriptionValue = isEdit ? editDescription : createDescription;
          return (
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-[10rem_1fr] gap-4 items-start mb-5">
                <label
                  id="checklist-title-label"
                  htmlFor="checklist-title"
                  className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold leading-[120%] text-[var(--Text-text-primary,#192048)] pt-2"
                >
                  {t("Title")}
                </label>
                <div className="flex flex-col gap-1">
                  <TextFieldComponent
                    id="checklist-title"
                    aria-labelledby="checklist-title-label"
                    placeholder={t("Enter Title")}
                    value={titleValue}
                    inputProps={{
                      maxLength: 70,
                    }}
                    onChange={(e) => (isEdit ? setEditTitle(e.target.value) : setCreateTitle(e.target.value))}
                    size="small"
                  />
                  {titleValue.length >= 70 && (
                    <span className="text-xs text-red-600">
                      {t("Please don't enter more than {{max, number}} characters", { max: 70 })}
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-[10rem_1fr] gap-4 items-start">
                <label
                  id="checklist-description-label"
                  htmlFor="checklist-description"
                  className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold leading-[120%] text-[var(--Text-text-primary,#192048)] pt-2"
                >
                  {t("Description")}
                </label>
                <div className="flex flex-col gap-1">
                  <TextFieldComponent
                    id="checklist-description"
                    aria-labelledby="checklist-description-label"
                    placeholder={t("Enter Description")}
                    value={descriptionValue}
                    slotProps={{
                      htmlInput: {
                        maxLength: 100,
                      },
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      isEdit ? setEditDescription(e.target.value) : setCreateDescription(e.target.value)
                    }
                    size="small"
                    multiline
                    minRows={6}
                  />
                  {descriptionValue.length >= 100 && (
                    <span className="text-xs text-red-600">
                      {t("Please don't enter more than {{max, number}} characters", { max: 100 })}
                    </span>
                  )}
                </div>
              </div>
            </form>
          );
        }

        case "delete":
          return <div>{t("Are you sure you want to delete the selected checklist(s)?")}</div>;

        default:
          return null;
      }
    };

    const modalFooter = () => {
      switch (modalState.type) {
        case "create":
          return (
            <div className="flex gap-4">
              <ButtonComponent
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                onClick={handleSubmit}
                loading={isSubmittingCreate}
                disabled={!createTitle.trim() || isSubmittingCreate}
              >
                {t("Create")}
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
                {t("Cancel")}
              </ButtonComponent>
            </div>
          );
        case "edit":
          return (
            <div className="flex gap-4">
              <ButtonComponent
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                onClick={handleSubmit}
                loading={isSubmittingEdit}
                disabled={!editTitle.trim() || isSubmittingEdit}
              >
                {t("Save Changes")}
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
                {t("Cancel")}
              </ButtonComponent>
            </div>
          );
        case "delete":
          return (
            <div className="flex gap-4">
              <ButtonComponent
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                onClick={performDeleteOperation}
                loading={isLoadingChecklist}
              >
                {t("Delete")}
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
                {t("Cancel")}
              </ButtonComponent>
            </div>
          );
        default:
          return null;
      }
    };

    const handleSelectedRowIdsChanged = () => {
      if (!_.get(dataGrid, "current.dataView")) return;
      const selected = dataGrid.current!.dataView.getAllSelectedItems();
      const selectedCount = selected.length;
      setHasSelection(selectedCount > 0);
      if (setIsRowSelected) setIsRowSelected(selectedCount > 0);
      setSelectedItems(selected);
      setRecordProperties((prev) => ({
        ...prev,
        // `selectedCount === 1` guarantees slot 0 exists.
        details: selectedCount === 1 ? selected[0]! : {},
      }));
    };

    const placeholderPanel = (
      <aside className="flex w-[27.6875rem] h-full items-center justify-center bg-white border-s border-gray-200 rounded-md shadow-sm">
        <div className="flex flex-col items-center text-center gap-2 px-6">
          <IconComponent name={"NoData"} className="cursor-pointer" width="20rem" height="20rem" />
          <p className="text-2xl font-medium text-gray-600">
            {t(selectedItems.length > 1 ? "Multiple Checklists selected" : "No Checklist selected")}
          </p>
          <p className="text-s text-[var(--Text-text-secondary,#667085)]">
            {t(
              selectedItems.length > 1
                ? "To view properties, select one."
                : "To view properties, you need to select one.",
            )}
          </p>
        </div>
      </aside>
    );

    const handleRecordClick = (args: GridClickEvent) => {
      if (args.detail.args.cell === 0) return;
      const rowData: ChecklistRow = dataGrid.current!.slickGrid.getDataItem(args.detail.args.row);
      navigate(getChecklistDetailUrl(rowData._id));
    };

    return (
      <div className="flex gap-4 h-full w-full">
        <GridPageLayout title="Reviews" description="Manage all settings related to Reviews module">
          <SlickGrid1
            ref={dataGrid}
            remoteDataSource={`${getBackendUrl()}/reviews/getChecklists`}
            query={checklistsQuery as QueryShape}
            columnDefinitions={checklistGridColumns as unknown as ValidatedColumnDefinition<ChecklistRow>[]}
            toolbar={(showOnlyGrid ? null : toolbarForChecklists) as ToolbarItem[]}
            infiniteScroll
            enableFilteringOption
            enableSortingOption
            propertiesPanel={!showOnlyGrid}
            toolbarClickHandler={(id: string) => {
              if (id === "properties") {
                handlePropertiesOpen();
              }
            }}
            onSelectedRowIdsChanged={handleSelectedRowIdsChanged}
            onClick={handleRecordClick}
          />
        </GridPageLayout>

        {recordProperties.show &&
          (selectedItems.length === 1 ? (
            <ChecklistProperties checklist={selectedItems[0]!} _id={selectedItems[0]?._id} dataGrid={dataGrid} />
          ) : (
            placeholderPanel
          ))}

        {modalState.show && (
          <ModalStateDialogComponent
            isVisible={modalState.show}
            allowModalPadding={false}
            width="50rem"
            headerTitle={modalHeader() || undefined}
            modelContent={modalContent() || undefined}
            onCancel={closeModal}
            footerContent={modalFooter() || undefined}
          />
        )}
      </div>
    );
  },
);

CheckListData.displayName = "CheckListData";

export default CheckListData;
